import React, { useEffect, useRef, useState } from 'react';
import { BrushCleaning, Loader2, Search, UserPlus } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { usePeopleSearch } from '../../../services/onboardingBoard';

// "Is this person already in our system, and where?" (moved off Lead
// Handoffs, 2026-09-28). Finds any lead or client by name, phone or email.
// A lead on the board opens there; someone in Nurture opens their row on
// the Nurture tab. Search only finds (Nic 2026-10-08): Re-engage lives on
// the Nurture list, where the reason and history are in view.

const STANDING = {
  making_contact: { label: 'Making Contact', cls: 'bg-sky-100 text-sky-700 dark:!bg-sky-500/15 dark:text-sky-300' },
  signing_up: { label: 'Signing Up', cls: 'bg-amber-100 text-amber-700 dark:!bg-amber-500/15 dark:text-amber-300' },
  nurture: { label: 'Nurture', cls: 'bg-violet-100 text-violet-700 dark:!bg-violet-500/15 dark:text-violet-300' },
  client: { label: 'Client', cls: 'bg-emerald-100 text-emerald-700 dark:!bg-emerald-500/15 dark:text-emerald-300' },
  dropped: { label: 'Dropped', cls: 'bg-rose-100 text-rose-700 dark:!bg-rose-500/15 dark:text-rose-300' },
};
const standingChip = (s) =>
  STANDING[s?.status] || {
    label: String(s?.status || 'lead').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
    cls: 'bg-slate-100 text-slate-600 dark:!bg-white/10 dark:text-slate-300',
  };

const formatPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p || '';
};

// The lookup moves that open a place on the board.
const OPENABLE = ['open', 'show_nurture', 'show_dropped'];

// The pipelines, in order (Nic 2026-10-20: make it clear who is in Sales,
// Client Onboarding, or Claims).
const GROUPS = [
  ['sales', 'Sales'],
  ['onboarding', 'Onboarding Workspace'],
  ['claims', 'Claims'],
  ['team', 'Team logins'],
];
// Stage colors (Nic 2026-10-20): a color means the same thing in every
// section, and statuses share one only when they mean the same thing. The
// group header says WHERE someone is; the color says HOW it's going.
//   grey    nobody is on it yet / not started
//   blue    we're working it
//   amber   waiting on them (the veteran, their records)
//   indigo  with the VA
//   violet  Nurture
//   green   done or won
//   orange  a problem that needs attention
//   red     ended
export const TONE_CLS = {
  grey: 'bg-slate-100 text-slate-600 dark:!bg-white/10 dark:text-slate-300',
  blue: 'bg-sky-100 text-sky-700 dark:!bg-sky-500/15 dark:text-sky-300',
  amber: 'bg-amber-100 text-amber-700 dark:!bg-amber-500/15 dark:text-amber-300',
  indigo: 'bg-indigo-100 text-indigo-700 dark:!bg-indigo-500/15 dark:text-indigo-300',
  violet: 'bg-violet-100 text-violet-700 dark:!bg-violet-500/15 dark:text-violet-300',
  green: 'bg-emerald-100 text-emerald-700 dark:!bg-emerald-500/15 dark:text-emerald-300',
  orange: 'bg-orange-100 text-orange-700 dark:!bg-orange-500/15 dark:text-orange-300',
  red: 'bg-rose-100 text-rose-700 dark:!bg-rose-500/15 dark:text-rose-300',
};
export const STAGE_TONE = {
  sales: {
    'New lead': 'grey',
    // Orphaned: nobody can see them but the search (Nic 2026-10-21).
    Orphaned: 'orange',
    'Signed up, no rep yet': 'grey',
    'Making Contact': 'blue',
    'Signing Up': 'amber',
    Nurture: 'violet',
    Dropped: 'red',
  },
  onboarding: {
    'Onboarding, unassigned': 'grey',
    'In Onboarding': 'blue',
    'Onboarding, re-engaged': 'blue',
    'Onboarding Nurture': 'violet',
    'Ready for CM': 'green',
  },
  claims: {
    Intake: 'grey',
    Client: 'grey',
    'Awaiting Medical Records': 'amber',
    'In Process': 'blue',
    'Awaiting Internal Review': 'blue',
    'Internal Review Approved': 'blue',
    'Submitted to VA': 'indigo',
    Invoiced: 'green',
    'Payment Plan': 'green',
    Paid: 'green',
    'Dispute Hold': 'orange',
    Dead: 'red',
  },
};
// Anything not in the table (a new status) reads grey until it's added.
export const stageTone = (pipeline, stage) => STAGE_TONE[pipeline]?.[stage] || 'grey';
const stageChip = (pipeline, stage) => TONE_CLS[stageTone(pipeline, stage)];

// Line 1: name and stage. Line 2: email · phone · who has them.
const SearchRow = ({ row, onPick }) => {
  const second = [row.email, formatPhone(row.phone), row.withName].filter(Boolean).join(' · ');
  const body = (
    <>
      <div className="flex items-center gap-2">
        <span className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{row.name}</span>
        {row.stage ? (
          <span className={cn('whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold', row.chipCls)}>
            {row.stage}
          </span>
        ) : null}
      </div>
      {second ? <div className="truncate text-xs text-slate-500 dark:text-slate-400">{second}</div> : null}
    </>
  );
  return onPick ? (
    <button
      type="button"
      onClick={onPick}
      className="block w-full px-3 py-1.5 text-left outline-none hover:bg-slate-50 focus:outline-none dark:hover:!bg-white/5"
    >
      {body}
    </button>
  ) : (
    <div className="px-3 py-1.5">{body}</div>
  );
};

// Controlled by the board (value / onChange): the same text also filters
// every tab's list (Nic 2026-09-28, one search instead of three).
const PeopleSearch = ({
  value: typed = '',
  onChange: setTyped,
  onOpenRun,
  onAddPerson = null,
  onOpenMatch = null,
}) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const search = usePeopleSearch(q);

  useEffect(() => {
    const t = setTimeout(() => setQ(typed.trim()), 250);
    return () => clearTimeout(t);
  }, [typed]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const results = search.data?.results || [];
  // Everyone else the search matched (2026-10-20: clients, signing-up
  // accounts, pool and Event leads; staff by full email), in the Add a
  // person form's words.
  const elsewhere = search.data?.elsewhere || [];
  const showPanel = open && q.length >= 2;

  // One row shape for everyone (Nic 2026-10-20): "Name (Stage)", then
  // email · phone · who has them, grouped by pipeline. Board results open
  // their card; a board person found by the lookup opens the same way; a
  // client or a team login is a note.
  const rows = [
    ...results.map((r) => ({
      key: `b-${r.leadId || r.userId}`,
      pipeline: 'sales',
      name: `${r.firstName || ''} ${r.lastName || ''}`.trim() || 'Unnamed',
      stage: r.stage || standingChip(r.standing).label,
      chipCls: stageChip('sales', r.stage || standingChip(r.standing).label),
      email: r.email,
      phone: r.phone,
      withName: r.withName,
      onPick: onOpenRun ? () => onOpenRun(r) : null,
    })),
    ...elsewhere
      .filter((m) => m.pipeline)
      .map((m, i) => ({
        key: `m-${m.leadId || ''}-${m.userId || ''}-${i}`,
        pipeline: m.pipeline,
        name: m.name || 'Unnamed',
        stage: m.stage,
        chipCls: stageChip(m.pipeline, m.stage),
        email: m.email,
        phone: m.phone,
        withName: m.withName,
        onPick: onOpenMatch && OPENABLE.includes(m.action) ? () => onOpenMatch(m) : null,
      })),
  ];
  const warnings = elsewhere.filter((m) => !m.pipeline);

  return (
    <div ref={rootRef} className="relative w-full">
      <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        value={typed}
        onChange={(e) => {
          setTyped(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search people"
        title="Search by name, phone or email"
        className={cn(
          'h-9 w-full rounded-full bg-white pl-8 pr-9 text-sm text-slate-700 outline-none dark:!bg-white/5 dark:text-slate-100',
          // Filtering: a steady blue outline so it is never forgotten.
          typed.trim()
            ? 'shadow-[0_0_0_1.5px_rgba(14,165,233,0.85)]'
            : 'ring-1 ring-slate-200 focus:ring-slate-300 dark:ring-white/10',
        )}
      />
      {typed ? (
        <button
          type="button"
          aria-label="Clear search"
          title="Clear search"
          onClick={() => {
            setTyped('');
            setQ('');
            setOpen(false);
          }}
          className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-sky-600 outline-none hover:bg-sky-50 focus:outline-none dark:text-sky-300 dark:hover:!bg-sky-500/15"
        >
          <BrushCleaning size={14} />
        </button>
      ) : null}

      {showPanel ? (
        <div className="absolute right-0 top-full z-30 mt-1.5 max-h-[26rem] w-[26rem] max-w-[90vw] overflow-y-auto rounded-xl bg-white py-1 shadow-[0_10px_40px_rgba(15,23,42,0.18)] ring-1 ring-slate-200/80 dark:!bg-surface-elevated dark:ring-white/10">
          {search.isLoading ? (
            <div className="flex h-20 items-center justify-center">
              <Loader2 size={16} className="animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              {/* "Different people" / "never linked" warnings sit on top. */}
              {warnings.map((w, i) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={`warn-${i}`}
                  data-testid="search-warning"
                  className="mx-2 my-1.5 rounded-lg bg-amber-50 px-2.5 py-2 text-xs text-amber-900 ring-1 ring-amber-200 dark:!bg-amber-500/10 dark:text-amber-100 dark:ring-amber-500/30"
                >
                  <div className="font-semibold">{w.title}</div>
                  {w.detail ? <div className="opacity-80">{w.detail}</div> : null}
                </div>
              ))}
              {!rows.length && !warnings.length ? (
                <div className="px-4 py-5 text-center text-sm text-slate-400">Nobody matches that.</div>
              ) : null}
              {GROUPS.map(([pipeline, label]) => {
                const inGroup = rows.filter((r) => r.pipeline === pipeline);
                if (!inGroup.length) return null;
                return (
                  <div key={pipeline} data-testid={`search-group-${pipeline}`} className="py-1">
                    <div className="px-3 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      {label}
                    </div>
                    {inGroup.map((r) => (
                      <SearchRow
                        key={r.key}
                        row={r}
                        onPick={
                          r.onPick
                            ? () => {
                                setOpen(false);
                                r.onPick();
                              }
                            : null
                        }
                      />
                    ))}
                    {pipeline === 'sales' && search.data?.total > results.length ? (
                      <div className="px-3 py-1 text-[11px] text-slate-400">
                        Showing {results.length} of {search.data.total} on the board. Type more to narrow it down.
                      </div>
                    ) : null}
                  </div>
                );
              })}
              {search.data?.elsewhereTotal > elsewhere.length ? (
                <div className="px-3 py-1 text-[11px] text-slate-400">
                  Showing {elsewhere.length} of {search.data.elsewhereTotal} others. Type more to narrow it down.
                </div>
              ) : null}
            </>
          )}
          {/* Add a person (Nic 2026-10-20): always under the results, so
              the rep has looked before adding. The form checks again. */}
          {onAddPerson && !search.isLoading ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onAddPerson(typed.trim());
              }}
              className="mt-1 flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2.5 text-left text-sm font-semibold text-sky-700 outline-none hover:bg-sky-50 focus:outline-none dark:border-white/10 dark:text-sky-300 dark:hover:!bg-sky-500/10"
            >
              <UserPlus size={15} />
              <span>
                {results.length ? 'Not them? Add someone to ' : 'Add someone to '}
                <span className="font-bold text-sky-800 dark:text-sky-200">Signing Up</span>
              </span>
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default PeopleSearch;
